"use client";

import {
  sdkActionManager,
  useEmbedNonStylesConfig,
  useEmbedStyles,
  useIsEmbed,
} from "@calcom/embed-core/embed-iframe";
import { useRouterQuery } from "@calcom/lib/hooks/useRouterQuery";
import useTheme from "@calcom/lib/hooks/useTheme";
import { UserAvatar } from "@calcom/ui/components/avatar";
import { UnpublishedEntity } from "@calcom/ui/components/unpublished-entity";
import { EventTypeDescriptionLazy as EventTypeDescription } from "@calcom/web/modules/event-types/components";
import EmptyPage from "@calcom/web/modules/event-types/components/EmptyPage";
import type { getServerSideProps } from "@server/lib/[user]/getServerSideProps";
import type { InferGetServerSidePropsType } from "next";
import Link from "next/link";
import { Toaster } from "sonner";

export type PageProps = InferGetServerSidePropsType<typeof getServerSideProps>;

const RESOURCES = [
  {
    label: "Annuities",
    desc: "Guaranteed retirement income",
    href: "https://veralifeig.com/products/annuities",
    icon: "M13 7h8m0 0v8m0-8l-8 8-4-4-6 6",
  },
  {
    label: "Life Insurance",
    desc: "Coverage guides and rates",
    href: "https://veralifeig.com/life-insurance",
    icon: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6",
  },
  {
    label: "Disability Coverage",
    desc: "Protect your income",
    href: "https://veralifeig.com/quote-disability",
    icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
  },
  {
    label: "Group Benefits",
    desc: "Employer & government plans",
    href: "https://veralifeig.com/group-benefits",
    icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z",
  },
];

export function UserPage(props: PageProps) {
  const { users, profile, eventTypes, entity } = props;
  const [user] = users;
  useTheme(profile.theme);

  const isBioEmpty = !user.bio || !user.bio.replace("<p><br></p>", "").length;
  const isEmbed = useIsEmbed(props.isEmbed);
  const eventTypeListItemEmbedStyles = useEmbedStyles("eventTypeListItem");
  const { user: _user, orgSlug: _orgSlug, redirect: _redirect, ...query } = useRouterQuery();

  if (entity.considerUnpublished) {
    return (
      <div className="flex h-full min-h-[calc(100dvh)] items-center justify-center">
        <UnpublishedEntity {...entity} />
      </div>
    );
  }

  const isEventListEmpty = eventTypes.length === 0;

  return (
    <>
      <div style={{ minHeight: "100vh", background: "#f4f1eb" }}>

        {/* NAV HEADER */}
        <header style={{ background: "#0b1733", padding: "14px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <img src="/veralife-logo-light.png" alt="VeraLife Insurance Group" style={{ height: "36px", objectFit: "contain" }} />
          <a
            href="https://veralifeig.com/quote-now"
            style={{ background: "#d9a93a", color: "#0b1733", fontWeight: 700, fontSize: "13px", padding: "8px 18px", borderRadius: "6px", textDecoration: "none", letterSpacing: "0.02em" }}>
            Get a Free Quote
          </a>
        </header>

        <div style={{ maxWidth: "680px", margin: "0 auto", padding: "32px 16px 0" }}>

          {/* HERO / PROFILE CARD */}
          <div style={{ background: "#0b1733", borderRadius: "16px", padding: "28px", display: "flex", gap: "20px", alignItems: "flex-start", marginBottom: "16px" }}>
            <UserAvatar
              size="xl"
              user={{ avatarUrl: user.avatarUrl, profile: user.profile, name: profile.name, username: profile.username }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "4px", flexWrap: "wrap" }}>
                <h1 style={{ color: "#ffffff", fontSize: "20px", fontWeight: 700, margin: 0 }}>{profile.name}</h1>
                <span style={{ background: "#d9a93a", color: "#0b1733", fontSize: "10px", fontWeight: 700, padding: "2px 8px", borderRadius: "4px", letterSpacing: "0.08em", textTransform: "uppercase" as const }}>
                  Licensed Agent
                </span>
              </div>
              <p style={{ color: "#d9a93a", fontSize: "12px", margin: "0 0 12px", letterSpacing: "0.04em" }}>
                NPN 21426840 · VeraLife Insurance Group
              </p>
              {!isBioEmpty && (
                <div
                  style={{ color: "rgba(255,255,255,0.75)", fontSize: "13px", lineHeight: "1.6" }}
                  dangerouslySetInnerHTML={{ __html: props.safeBio }}
                />
              )}
            </div>
          </div>

          {/* RESOURCES GRID */}
          <p style={{ color: "#6b7280", fontSize: "11px", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase" as const, margin: "24px 0 10px" }}>
            Resources
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "10px", marginBottom: "24px" }}>
            {RESOURCES.map((r) => (
              <a
                key={r.href}
                href={r.href}
                target="_blank"
                rel="noopener noreferrer"
                style={{ background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: "10px", padding: "14px 16px", textDecoration: "none", display: "flex", gap: "12px", alignItems: "center" }}>
                <span style={{ width: "36px", height: "36px", background: "#fef3c7", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <svg width="18" height="18" fill="none" stroke="#b78623" strokeWidth="1.8" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d={r.icon} />
                  </svg>
                </span>
                <div>
                  <div style={{ color: "#0b1733", fontSize: "13px", fontWeight: 600 }}>{r.label}</div>
                  <div style={{ color: "#9ca3af", fontSize: "11px", marginTop: "1px" }}>{r.desc}</div>
                </div>
              </a>
            ))}
          </div>

          {/* BOOK A CALL */}
          <p style={{ color: "#6b7280", fontSize: "11px", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase" as const, margin: "0 0 10px" }}>
            Book a Call
          </p>
          <div
            style={{ borderRadius: "12px", overflow: "hidden", border: "1px solid #e5e7eb", marginBottom: "48px" }}
            data-testid="event-types">
            {eventTypes.map((type) => (
              <Link
                key={type.id}
                style={{ display: "flex", ...eventTypeListItemEmbedStyles }}
                prefetch={false}
                href={{ pathname: `/${user.profile.username}/${type.slug}`, query }}
                passHref
                onClick={async () => { sdkActionManager?.fire("eventTypeSelected", { eventType: type }); }}
                data-testid="event-type-link">
                <div style={{ background: "#ffffff", borderBottom: "1px solid #f3f4f6", padding: "18px 20px", width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "4px" }}>
                      <span style={{ width: "8px", height: "8px", background: "#d9a93a", borderRadius: "50%", flexShrink: 0 }} />
                      <h2 style={{ color: "#0b1733", fontSize: "14px", fontWeight: 700, margin: 0 }}>{type.title}</h2>
                    </div>
                    <EventTypeDescription eventType={type} isPublic={true} shortenDescription />
                  </div>
                  <svg style={{ width: "16px", height: "16px", color: "#d9a93a", flexShrink: 0, marginLeft: "12px" }} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </Link>
            ))}
            {isEventListEmpty && <EmptyPage name={profile.name || "User"} />}
          </div>

          {/* FOOTER */}
          <div style={{ textAlign: "center" as const, paddingBottom: "32px" }}>
            <img src="/veralife-logo.png" alt="VeraLife" style={{ height: "26px", objectFit: "contain", opacity: 0.5, marginBottom: "8px", display: "block", margin: "0 auto 8px" }} />
            <p style={{ color: "#9ca3af", fontSize: "11px", margin: 0 }}>
              2026 VeraLife Insurance Group · Licensed in multiple states
            </p>
          </div>
        </div>
      </div>
      <Toaster position="bottom-right" />
    </>
  );
}

export default UserPage;
